// src/pages/AnnouncementCreatePage.js

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "api";
import { Preferences } from "@capacitor/preferences";
import { Check } from "phosphor-react";
import BackButton from "../components/BackButton";

async function getToken() {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}

  return localStorage.getItem("token") || sessionStorage.getItem("token") || null;
}

async function getAuthHeaders() {
  const t = await getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function AnnouncementCreatePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const canSave = title.trim() || body.trim();

  const goBackToCircle = () => {
    navigate(`/circles/${id}`, { replace: true });
  };

  const onSave = async (e) => {
    e?.preventDefault?.();

    const t = (title || "").trim();
    const b = (body || "").trim();
    if (!t && !b) return;

    const combined = t && b ? `${t}\n\n${b}` : t ? t : b;

    try {
      setError("");
      setSaving(true);

      const headers = await getAuthHeaders();

      await api.post(
        `/groups/${id}/announcements`,
        { body: combined, pinned: !!pinned },
        { headers }
      );

      navigate(`/circles/${id}/announcements`, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to post announcement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[100svh] bg-blue-50">
      <header
        className="sticky top-0 left-0 right-0 z-30 bg-blue-50"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mt-2 mx-auto px-4 flex items-center">
          <BackButton
            className="text-blue-600"
            ariaLabel="Back"
            onClick={() => navigate(-1)}
          />

          <h1 className="flex-1 text-left text-blue-600 font-bold leading-none tracking-[-0.01em] text-[20px] whitespace-nowrap">
            Create Announcement
          </h1>
        </div>
      </header>

      <main
        className="px-5 pt-1 pb-6"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 24px)" }}
      >
        <form onSubmit={onSave} className="space-y-2">
          <div>
            <label className="block text-sm font-medium mb-1 ml-3">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded-2xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Announcement title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 ml-3">
              Body
            </label>
            <textarea
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full border rounded-2xl p-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              placeholder="Write your announcement."
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-1">
            <label className="inline-flex cursor-pointer items-center gap-2 ml-2 text-sm font-medium text-blue-700 select-none">
              <input
                type="checkbox"
                checked={!!pinned}
                onChange={(e) => setPinned(e.target.checked)}
                className="peer sr-only"
              />

              <div className="flex h-4 w-4 items-center justify-center rounded-3xl bg-white border border-slate-200">
                <Check
                  size={12}
                  weight="bold"
                  className={`text-blue-500 bg-white rounded-full transition-opacity ${
                    pinned ? "opacity-100" : "opacity-0"
                  }`}
                />
              </div>

              <span>Pin this announcement</span>
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={goBackToCircle}
                className="!text-sm !font-medium !rounded-full !px-3 !min-h-0 !h-8 bg-white "
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={!canSave || saving}
                className="!bg-blue-600 !font-medium disabled:opacity-50 !text-white !text-sm !rounded-full !px-3 !min-h-0 !h-8"
              >
                {saving ? "Saving…" : "Create"}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </form>
      </main>
    </div>
  );
}